import {
  json,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
} from "@remix-run/node";
import {
  Form,
  Link,
  useLoaderData,
  useSearchParams,
  useSubmit,
} from "@remix-run/react";
import { Plus, Search } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast as sonnerToast } from "sonner";
import {
  CommunityAnnouncementBannerCarousel,
  CommunityBadgeFeed,
  CommunityChallengeCardCarousel,
  CommunityFeedItem,
  CommunityLeaderboard,
  CommunityRecentEngagement,
  CommunityStatsRail,
  CommunityWinnerSpotlightCarousel,
} from "#app/components/community/landing-sections.tsx";
import { Input as AntInput } from "#app/components/compat/antd";
import { PageHeader } from "#app/components/new-ui/page-header";
import { PaginationBlock } from "#app/components/new-ui/pagination-block";
import { Button } from "#app/components/ui/button";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "#app/components/ui/select.tsx";
import { requireUserId } from "#app/utils/auth.server.ts";
import { summarizeForumResults } from "#app/utils/community-ai.server.ts";
import {
  ensureForumCategories,
  getCommunityHighlights,
  getCommunityLeaderboard,
  getCommunityStats,
  getRecentBadgeAwards,
  getRecentCommunityEngagement,
} from "#app/utils/community.server.ts";
import { prisma } from "#app/utils/db.server.ts";
import { cn } from "#app/utils/misc.tsx";

export const handle = {
  i18n: ["community"],
};

export async function action({ request }: ActionFunctionArgs) {
  await requireUserId(request);
  const formData = await request.formData();
  const intent = formData.get("intent");

  if (intent !== "summarize-results") {
    return json({ error: "Unsupported action" }, { status: 400 });
  }

  const search = String(formData.get("q") || "").trim();
  const categoryId = String(formData.get("category") || "");
  const sort = String(formData.get("sort") || "newest");
  const page = Math.max(
    parseInt(String(formData.get("page") || "1"), 10) || 1,
    1,
  );
  const pageSize = 20;
  const take = 10;

  const where: any = { isHidden: false };
  if (search) {
    where.title = { contains: search, mode: "insensitive" };
  }
  if (categoryId && categoryId !== "all") {
    where.categoryId = categoryId;
  }

  const orderBy: any = {};
  if (sort === "trending" || sort === "views") orderBy.viewCount = "desc";
  else if (sort === "popular") orderBy.score = "desc";
  else orderBy.createdAt = "desc";

  const topics = await prisma.forumTopic.findMany({
    where,
    orderBy,
    select: {
      id: true,
      title: true,
      content: true,
      score: true,
      createdAt: true,
      _count: { select: { posts: true } },
    },
    take,
    skip: (page - 1) * pageSize,
  });

  if (topics.length === 0) {
    return json({ summary: null, topics: [] });
  }

  const question = search
    ? `Summarize community results for: ${search}`
    : "Summarize the current community topics.";

  const summary = await summarizeForumResults({
    question,
    topics: topics.map((t) => ({
      id: t.id,
      title: t.title,
      content: t.content,
    })),
  });

  return json({
    summary,
    topics: topics.map((t) => ({
      id: t.id,
      title: t.title,
      score: t.score,
      createdAt: t.createdAt,
      _count: t._count,
    })),
  });
}

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const search = url.searchParams.get("q");
  let categoryId = url.searchParams.get("category");
  const sort = url.searchParams.get("sort") || "newest";
  const page = Math.max(parseInt(url.searchParams.get("page") || "1", 10), 1);
  const pageSize = 20;

  const categories = await ensureForumCategories();

  const where: any = { isHidden: false };
  if (search) {
    where.title = { contains: search, mode: "insensitive" };
  }
  if (categoryId && categoryId !== "all") {
    where.categoryId = categoryId;
  }

  const orderBy: any = {};
  if (sort === "trending" || sort === "views") orderBy.viewCount = "desc";
  else if (sort === "popular") orderBy.score = "desc";
  else orderBy.createdAt = "desc";

  const [
    topicsCount,
    topics,
    stats,
    leaderboard,
    recentBadges,
    recentEngagement,
    highlights,
  ] = await Promise.all([
    prisma.forumTopic.count({ where }),
    prisma.forumTopic.findMany({
      where,
      orderBy,
      include: {
        author: {
          select: { id: true, name: true, username: true, image: true },
        },
        category: true,
        _count: { select: { posts: true } },
      },
      take: pageSize,
      skip: (page - 1) * pageSize,
    }),
    getCommunityStats(),
    getCommunityLeaderboard(3),
    getRecentBadgeAwards(3),
    getRecentCommunityEngagement(10),
    getCommunityHighlights(),
  ]);

  return json({
    categories,
    topics,
    search,
    categoryId,
    sort,
    page,
    pageSize,
    topicsCount,
    stats,
    leaderboard,
    recentBadges,
    recentEngagement,
    ...highlights,
  });
}

export default function CommunityIndex() {
  const {
    categories,
    topics,
    search,
    categoryId,
    sort,
    page,
    pageSize,
    topicsCount,
    stats,
    leaderboard,
    recentBadges,
    recentEngagement,
    heroAnnouncements,
    heroChallenges,
    winnerSpotlights,
  } = useLoaderData<typeof loader>();
  const { t } = useTranslation("community");
  const [searchParams, setSearchParams] = useSearchParams();
  const submit = useSubmit();
  const totalPages = Math.max(Math.ceil(topicsCount / pageSize), 1);

  const topicDeletedToast = searchParams.get("toast");
  useEffect(() => {
    if (topicDeletedToast !== "topic-deleted") return;
    sonnerToast.success(t("topicDeleted"), {
      description: t("topicDeletedDesc"),
    });
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete("toast");
        return p;
      },
      { replace: true },
    );
  }, [topicDeletedToast, setSearchParams, t]);

  const listingCategories = useMemo(() => [...categories], [categories]);

  const isSortTabActive = (tabSort: string) =>
    sort === tabSort && (!categoryId || categoryId === "all");

  const tabClassName = (active: boolean) =>
    cn(
      "rounded-t-lg px-4 py-2 text-sm transition-colors",
      active
        ? "border-b-2 border-foreground -mb-px font-medium text-foreground"
        : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800",
    );

  const updateParams = (updates: Record<string, string>) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        params.set(key, value);
      }
      return params;
    });
  };

  const buildCategoryLink = (nextCategory: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("category", nextCategory);
    params.set("page", "1");
    return `?${params.toString()}`;
  };

  const buildTabLink = (newSort: string, newCategory: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("sort", newSort);
    params.set("category", newCategory);
    params.set("page", "1");
    return `?${params.toString()}`;
  };

  return (
    // Was a self-framed card with its own header bar and inner scroller; the
    // shell's content column already scrolls, and a nested one broke
    // PageHeader's `sticky top-0`.
    <main className="community-block space-y-6 pb-6">
      <PageHeader
        showOrganization
        keepRightOnTablet
        left={
          <div className="flex min-w-0 items-center gap-3">
            {/* <HamburgerMenu /> */}
            <h1 className="truncate py-4 font-medium">{t("title")}</h1>
          </div>
        }
        right={
          <Button
            variant="gradient"
            size="sm"
            className="gap-1.5 text-xs text-white!"
            render={<Link to="new" />}
          >
            <Plus className="size-4" />
            {t("createPost")}
          </Button>
        }
      />

      <div className="px-6">
        <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
      </div>

      <div className="px-6">
        <div className="gap-6 xl:grid xl:grid-cols-[minmax(0,1.6fr)_360px]">
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex sm:flex-wrap gap-2 border-b border-slate-200 pb-px overflow-x-auto dark:border-slate-800">
                <Link
                  to={buildTabLink("trending", "all")}
                  title={t("tabs.trendingTitle")}
                  className={tabClassName(isSortTabActive("trending"))}
                >
                  {t("tabs.trending")}
                </Link>
                <Link
                  to={buildTabLink("popular", "all")}
                  title={t("tabs.popularTitle")}
                  className={tabClassName(isSortTabActive("popular"))}
                >
                  {t("tabs.popular")}
                </Link>
                <Link
                  to={buildTabLink("newest", "all")}
                  className={tabClassName(isSortTabActive("newest"))}
                >
                  {t("tabs.latest")}
                </Link>
                {listingCategories.map((category: any) => (
                  <Link
                    key={category.id}
                    to={buildCategoryLink(category.id)}
                    className={cn(
                      tabClassName(categoryId === category.id),
                      "shrink-0",
                    )}
                  >
                    {category.slug
                      ? t(`categoryNames.${category.slug}`, {
                          defaultValue: category.name,
                        })
                      : category.name}
                  </Link>
                ))}
              </div>
            </div>

            <CommunityAnnouncementBannerCarousel
              announcements={heroAnnouncements}
            />
            <CommunityChallengeCardCarousel challenges={heroChallenges} />
            <CommunityWinnerSpotlightCarousel challenges={winnerSpotlights} />

            <div className="space-y-5">
              <div className="flex flex-col gap-3 xl:flex-row">
                <Form
                  onChange={(event) => submit(event.currentTarget)}
                  className="flex-1"
                >
                  <AntInput
                    name="q"
                    prefix={<Search size={16} className="text-slate-400" />}
                    placeholder={t("searchPlaceholder")}
                    className="global-search-input max-w-full! xl:max-w-[20rem]!"
                    defaultValue={search || ""}
                  />
                  <input type="hidden" name="page" value="1" />
                  <input
                    type="hidden"
                    name="category"
                    value={categoryId || "all"}
                  />
                  <input type="hidden" name="sort" value={sort} />
                </Form>

                <Select
                  value={sort}
                  onValueChange={(value) =>
                    updateParams({ sort: value ?? "", page: "1" })
                  }
                >
                  <SelectTrigger className="w-full shrink-0 xl:w-[180px]">
                    <SelectValue placeholder={t("sortBy")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trending">
                      {t("sort.trending")}
                    </SelectItem>
                    <SelectItem value="popular">{t("sort.popular")}</SelectItem>
                    <SelectItem value="newest">{t("sort.newest")}</SelectItem>
                    <SelectItem value="views">{t("sort.views")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      {t("latestActivity")}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {t("discussionsMatch", { count: topicsCount })}
                    </p>
                  </div>
                </div>

                {topics.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
                    {t("emptyTopics")}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {topics.map((topic: any) => (
                      <CommunityFeedItem key={topic.id} topic={topic} />
                    ))}
                  </div>
                )}
              </div>

              <PaginationBlock
                className="px-0"
                page={page}
                totalPages={totalPages}
                onPageChange={(nextPage) =>
                  updateParams({ page: String(nextPage) })
                }
              />
            </div>
          </div>

          <div className="space-y-6 mt-10 border-t py-10 sm:border-t-0 sm:py-0 sm:mt-0">
            <CommunityStatsRail stats={stats} topContributors={leaderboard} />
            <CommunityBadgeFeed badges={recentBadges} />
            <CommunityLeaderboard items={leaderboard} />
            <CommunityRecentEngagement items={recentEngagement} />
          </div>
        </div>
      </div>
    </main>
  );
}
